/**
 * ReceiptsTab - Admin receipts management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { Receipt, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';

export function ReceiptsTab({ 
  receipts,
  onResendReceipt,
  getStatusIcon,
  getStatusColor,
  formatDate
}) {
  const { t } = useLanguage();

  return (
    <motion.div 
      key="receipts-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" 
      data-testid="receipts-table"
    >
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900">{t('dashboard.admin.receipts')}</h3>
      </div>
      {receipts.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>{t('tables.noReceiptsYet')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.receiptId')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.beneficiary')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.amount')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {receipts.map((receipt) => (
                <tr key={receipt.id} className="hover:bg-slate-50" data-testid={`receipt-${receipt.receipt_id}`}>
                  <td className="px-6 py-4 font-mono text-sm text-slate-900">{receipt.receipt_id}</td>
                  <td className="px-6 py-4 text-slate-900">{receipt.recipient_masked}</td>
                  <td className="px-6 py-4 font-semibold text-slate-900">${receipt.amount?.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(receipt.status)}`}>
                      {getStatusIcon(receipt.status)}
                      {t(`dashboard.status.${receipt.status}`) || receipt.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">{formatDate(receipt.created_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Link to={`/receipt/${receipt.receipt_id}`} target="_blank">
                        <Button size="sm" variant="outline">{t('common.view')}</Button>
                      </Link>
                      {onResendReceipt && (
                        <Button size="sm" variant="outline" onClick={() => onResendReceipt(receipt.receipt_id)}>
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}

export default ReceiptsTab;
